import { DateTime } from 'luxon';
import { resolveVisitZone, VISIT_PAST_RETENTION_DAYS, VisitBrowseRow } from './visit.browse';

/** A Visit-only appointment window. The recurring occurrence identity stays on localDate/sourceKey. */
export interface VisitDateOverride {
    startDate: Date;
    endDate: Date;
    allDay: boolean;
    /** Server-stamped when a person decides this window; never caller-writable. */
    decidedAt?: Date;
}

export interface VisitDatesRequest {
    id: string;
    dates?: VisitDateOverride;
    restore?: boolean;
    /** Section 15: the person confirmed a date outside the Visit's compliance window. */
    allowOutsideWindow?: boolean;
}

export const VISIT_DATES_COMMAND = 'VisitDatesCommand';

export type VisitRescheduleSubject = Pick<VisitBrowseRow, 'localDate' | 'timezone'>;

/**
 * Earliest local ISO date a reschedule may pick, in the Visit's timezone: any date from today on, or a
 * past date no earlier than the original date and never before the read floor. Past that floor the
 * forecast has dropped the occurrence, so the moved Visit could not be read back.
 */
export function visitRescheduleFloor(row: VisitRescheduleSubject, now: Date): string {

    const today = DateTime.fromJSDate(now, { zone: resolveVisitZone(row.timezone) }).startOf('day');
    const retention = today.minus({ days: VISIT_PAST_RETENTION_DAYS }).toISODate()!;
    const pastFloor = row.localDate > retention ? row.localDate : retention;
    const todayIso = today.toISODate()!;
    return pastFloor < todayIso ? pastFloor : todayIso;
}

/** `localDate` is the new appointment's local ISO date in the Visit's timezone. */
export function visitRescheduleAllowed(row: VisitRescheduleSubject, localDate: string, now: Date): boolean {

    return localDate >= visitRescheduleFloor(row, now);
}

/** Bulk date changes preserve each Visit's local times and overnight day span, including across DST. */
export function visitWindowOnDate(row: Pick<VisitBrowseRow, 'timezone' | 'allDay' | 'startDate' | 'endDate'>, localDate: string): VisitDateOverride {

    const day = DateTime.fromISO(localDate, { zone: row.timezone }).startOf('day');
    if (row.allDay) return { startDate: day.toJSDate(), endDate: day.plus({ days: 1 }).toJSDate(), allDay: true };
    const start = DateTime.fromJSDate(new Date(row.startDate), { zone: row.timezone });
    const end = DateTime.fromJSDate(new Date(row.endDate), { zone: row.timezone });
    const spanDays = Math.max(0, Math.round(end.startOf('day').diff(start.startOf('day'), 'days').days));
    const nextStart = day.set({ hour: start.hour, minute: start.minute });
    const nextEnd = day.plus({ days: spanDays }).set({ hour: end.hour, minute: end.minute });
    return { startDate: nextStart.toJSDate(), endDate: nextEnd.toJSDate(), allDay: false };
}
